// eslint-disable-next-line no-restricted-imports
import { coerce, compare as semverCompare, inc, parse, satisfies as semverSatisfies, valid, validRange } from "semver";

const normalize = (v: string | null) => {
    return v?.trim().toLowerCase() ?? "";
};

/**
 * Matches strings that look like loose version numbers with dot-separated digits (e.g. "1", "1.10", "v2.0.0").
 * The digit-dot sequence may be followed by a `-` or `_` suffix (e.g. "1.0.0-beta", "1.0.0_abc").
 * Only these forms are passed to `semver.coerce`.
 * Arbitrary strings like "TB305FU" are rejected so that embedded digits are not mistakenly extracted into a semver.
 * Otherwise coerce would turn "TB305FU" into "305.0.0".
 */
const CoerceVersionRegex = /^v?\d+(\.\d+)*([-_]|$)/i;

const toSemver = (v: string) => {
    if (!v) {
        return null;
    }
    if (valid(v)) {
        return v;
    }
    if (CoerceVersionRegex.test(v)) {
        return coerce(v)?.version ?? null;
    }
    return null;
};

/**
 * Sorts version strings, treating any value that is not valid semver as "older" than valid semver.
 * Some third-party devices may not comply with semver format, so we need to handle both cases.
 *
 * Each value is first run through `semver.coerce` to turn loose forms into strict semver:
 * "5" → "5.0.0", "v1.0.0" → "1.0.0", "1.10" → "1.10.0". Without coercion, "1.10" and "1.2"
 * would both fail `semver.valid` and fall back to `localeCompare`, which compares them
 * character-by-character ("1" vs "2") and wrongly orders "1.10" before "1.2".
 *
 * Two-semver values are compared via semver, two non-semver values via locale-aware string
 * compare, and in mixed pairs the non-semver value always sorts before the semver one.
 */
const compare = (a: string | null, b: string | null): number => {
    const versionA = normalize(a);
    const versionB = normalize(b);

    const semverA = toSemver(versionA);
    const semverB = toSemver(versionB);

    if (semverA && semverB) {
        return semverCompare(semverA, semverB);
    }

    if (!semverA && !semverB) {
        return versionA.localeCompare(versionB, "en");
    }

    return semverA ? 1 : -1;
};

const getMajorVersion = (v: string | null | undefined) => {
    if (!v) {
        return null;
    }
    const normalized = normalize(v);
    const semver = toSemver(normalized);
    const parsed = parse(semver);
    return parsed ? parsed.major : null;
};

/**
 * Checks a version against a range such as ">=16". Returns `null` when either side cannot be
 * interpreted, so callers can tell "does not match" apart from "unknown".
 * Prereleases are included: a device on "16.0.0-beta" satisfies ">=16".
 */
const satisfies = (version: string | null | undefined, range: string | null | undefined): boolean | null => {
    const semver = toSemver(normalize(version ?? null));
    const trimmedRange = range?.trim();
    if (!semver || !trimmedRange || !validRange(trimmedRange)) {
        return null;
    }
    return semverSatisfies(semver, trimmedRange, { includePrerelease: true });
};

export default { compare, inc, parse, getMajorVersion, satisfies };
